import { Box, ButtonGroup, Flex } from "@chakra-ui/react";
import React from "react";
import { MdOpenInNew } from "react-icons/md";

import { Button } from "../../../components";

export enum VideoIntegration {
  GOOGLE_MEET = "GOOGLE_MEET",
  ZOOM = "ZOOM",
  TEAMS = "TEAMS",
}

const platformNameMap = {
  [VideoIntegration.GOOGLE_MEET]: "Google Meet",
  [VideoIntegration.TEAMS]: "Teams",
  [VideoIntegration.ZOOM]: "Zoom",
};

const IntegrationImage: React.FC<
  Pick<IntegrationMessageProps, "videoIntegration" | "isError">
> = ({ videoIntegration, isError }) => {
  if (videoIntegration === VideoIntegration.GOOGLE_MEET) {
    if (isError) {
      return (
        <img src="/static/images/alert-circle-google-meet.svg" width="146px" />
      );
    }
    return <img src="/static/images/google-meet-record.svg" width="146px" />;
  }
  if (videoIntegration === VideoIntegration.TEAMS) {
    if (isError) {
      return <img src="/static/images/alert-circle-teams.svg" width="146px" />;
    }
    return <img src="/static/images/teams-record.svg" width="146px" />;
  }
  if (isError) {
    return <img src="/static/images/alert-circle-zoom.svg" width="144px" />;
  }
  return <img src="/static/images/zoom-record.svg" width="300px" />;
};

const ActionButtons: React.FC<
  Pick<
    IntegrationMessageProps,
    | "videoIntegration"
    | "launchButtonMessage"
    | "videoMeetingUrl"
    | "showLaunchButton"
    | "showImportButton"
    | "inZoomApp"
    | "inTeamsApp"
  >
> = ({
  videoIntegration,
  launchButtonMessage,
  videoMeetingUrl,
  showLaunchButton,
  showImportButton,
  inZoomApp,
  inTeamsApp,
}) => {
  const platformName = platformNameMap[videoIntegration];

  if (showImportButton) {
    if (inZoomApp || inTeamsApp) {
      return (
        <Button
          data-testid="importMeetingButton"
          mt="8"
          onClick={() => {
            window.location.href = `${window.location.href}&import=true`;
          }}
        >
          Record this meeting
        </Button>
      );
    }
    return (
      <ButtonGroup mt="8" gap={4} alignItems="center" flexDirection="column">
        <Button
          data-testid="importMeetingButton"
          onClick={() => {
            window.location.href = `${window.location.href}&import=true`;
          }}
          rightIcon={<MdOpenInNew />}
        >
          Open {platformName} and record this meeting
        </Button>
        {videoMeetingUrl && (
          <Button
            data-testid="openMeetingButton"
            variant="outline"
            onClick={() => {
              window.open(videoMeetingUrl);
            }}
            rightIcon={<MdOpenInNew />}
          >
            Open {platformName} without recording
          </Button>
        )}
      </ButtonGroup>
    );
  }

  if (showLaunchButton && videoMeetingUrl) {
    return (
      <>
        <Button
          data-testid="openMeetingButton"
          mt="8"
          onClick={() => {
            window.open(videoMeetingUrl);
          }}
          rightIcon={<MdOpenInNew />}
        >
          Open {platformName}
        </Button>

        {launchButtonMessage && (
          <Box fontWeight="400" fontSize="sm" mt="7" mx="10">
            {launchButtonMessage}
          </Box>
        )}
      </>
    );
  }

  return null;
};

export type IntegrationMessageProps = {
  videoIntegration: VideoIntegration;
  primaryMessage: string;
  actionMessage?: string;
  secondaryMessage?: string;
  launchButtonMessage?: string;
  isError?: boolean;
  videoMeetingUrl?: string | null;
  showLaunchButton?: boolean;
  showImportButton?: boolean;
  inZoomApp?: boolean;
  inTeamsApp?: boolean;
};

export const IntegrationMessage: React.FC<IntegrationMessageProps> = ({
  videoIntegration,
  primaryMessage,
  actionMessage,
  secondaryMessage,
  launchButtonMessage,
  isError,
  videoMeetingUrl,
  showLaunchButton,
  showImportButton,
  inZoomApp,
  inTeamsApp,
}) => {
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      p={6}
      pt={0}
      position="relative"
      top={isError ? "-2rem" : "-1rem"}
    >
      <IntegrationImage videoIntegration={videoIntegration} isError={isError} />
      <Box
        fontWeight="700"
        fontSize="24px"
        mt={isError ? "3.5rem" : "3rem"}
        maxWidth={isError ? "90%" : "80%"}
      >
        <Box data-testid="primary" as="span">
          {primaryMessage}
        </Box>
        &nbsp;
        {actionMessage && (
          <Box data-testid="action" as="span" color="blue.600">
            {actionMessage}
          </Box>
        )}
      </Box>
      {secondaryMessage && (
        <Box
          color="primary"
          data-testid="secondary"
          fontWeight="400"
          fontSize="16px"
          mt="8"
          mx={10}
        >
          {secondaryMessage}
        </Box>
      )}
      <ActionButtons
        videoIntegration={videoIntegration}
        launchButtonMessage={launchButtonMessage}
        videoMeetingUrl={videoMeetingUrl}
        showLaunchButton={showLaunchButton}
        showImportButton={showImportButton}
        inZoomApp={inZoomApp}
        inTeamsApp={inTeamsApp}
      />
    </Flex>
  );
};
